import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, Check, MapPin, Clock, Mail, ShieldCheck, Star, ArrowRight,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import TrustStrip from "../components/TrustStrip";
import { brand, heroImage, inspection } from "../data/site";
import { serviceAreas } from "../data/serviceAreas";

// Standalone Spanish landing page (/espanol). The rest of the site stays in
// English; this page carries the whole story for Spanish-speaking homeowners:
// hero, form, services, reasons to choose Pepla, cities, and contact info.

const servicios = [
  { t: "Techos Residenciales", d: "Reparación, reemplazo y sistemas completos de teja, shingle, lámina y techos planos." },
  { t: "Reparación de Techos", d: "Goteras, tejas rotas, sellados, tapajuntas y valles." },
  { t: "Reemplazo de Fieltro (Underlayment)", d: "Levantamos su teja, cambiamos el fieltro dañado y volvemos a colocar la misma teja." },
  { t: "Techos de Foam y Planos", d: "Recubrimiento (recoating), reparación de ampollas y corrección de encharcamiento." },
  { t: "Inspecciones para Venta (BINSR)", d: "Inspección con fotos y precios por partida, dentro del plazo de la venta." },
];

const razones = [
  "Inspecciones y estimados GRATIS, los 7 días de la semana de 7am a 7pm (le llamamos para confirmar la hora)",
  "Techos de teja, asfalto, planos y foam — reparación y reemplazo",
  "Reemplazo de fieltro (underlayment) bajo su teja existente",
  "Cada producto se nombra por marca, línea y color antes de firmar",
  "Equipos con experiencia y sitios de trabajo limpios",
  "Portal privado para cada trabajo: vea sus estimados y facturas, el avance con fotos y el mantenimiento pendiente",
  "Financiamiento disponible a través de Enhancify para proyectos que califiquen",
  "Atención en español",
];

const Espanol = () => {
  const ciudades = serviceAreas.slice(0, 18);

  return (
    <div>
      <SEO
        title="Techos en Phoenix, AZ | Hablamos Español"
        description={`Reparación y reemplazo de techos de teja, shingle, foam y planos en todo el condado de Maricopa. Inspecciones gratis. Hablamos español: ${brand.phone}.`}
        lang="es"
        alternate={{ en: `${SITE_URL}/`, es: `${SITE_URL}/espanol` }}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Pepla Roofing en Español",
          url: `${SITE_URL}/espanol`,
          inLanguage: "es",
          publisher: { "@id": `${SITE_URL}/#business` },
        }}
      />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center section-dark text-white overflow-hidden">
        <div className="absolute inset-0">
          <img fetchPriority="high" decoding="async" src={heroImage} alt="Casa en Arizona con techo nuevo de teja" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full pt-32 pb-16">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center text-white text-xs font-semibold tracking-[0.25em] uppercase mb-5">
              Arizona · Hablamos Español
            </span>
            <h1 className="font-heading text-white text-5xl md:text-7xl uppercase leading-[0.95] tracking-tight mb-6">
              Techos en Phoenix, <span className="text-brand">Bien Hechos</span>
            </h1>
            <p className="text-white/75 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              {brand.name} repara, reemplaza e inspecciona techos de teja, shingle, foam y planos
              en todo el condado de Maricopa. Inspecciones y estimados gratis, y atención en español.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${brand.phoneRaw}`}
                className="inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-5 rounded-md transition-colors"
              >
                <Phone size={20} /> Llame Ahora · {brand.phone}
              </a>
              <a
                href={`mailto:${brand.emailRaw}`}
                className="inline-flex items-center justify-center gap-3 border border-white/25 hover:border-brand hover:text-brand font-semibold uppercase tracking-wider text-sm px-8 py-5 rounded-md transition-colors"
              >
                <Mail size={20} /> Escríbanos
              </a>
            </div>
            <TrustStrip className="mt-10" points={["Inspecciones y estimados gratis", "7 días a la semana, 7am–7pm", "Todo por escrito"]} labels={{ rated: "Calificación", outOf: "de 5 en Google", onGoogle: "en Google Reviews" }} />
          </div>
        </div>
      </section>

      {/* FORM + WHY */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <span className="kicker">Por Qué Pepla</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              Trabajo Bien Hecho y Respaldado
            </h2>
            <div className="brand-line mb-6" />
            <ul className="space-y-4">
              {razones.map((r) => (
                <li key={r} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={14} className="text-brand" />
                  </span>
                  <span className="leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 space-y-4 text-sm">
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-brand shrink-0" />
                <a href={`tel:${brand.phoneRaw}`} className="font-semibold hover:text-brand transition-colors">{brand.phone}</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-brand shrink-0" />
                <a href={`mailto:${brand.emailRaw || brand.email}`} className="hover:text-brand transition-colors">{brand.email}</a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-brand shrink-0 mt-0.5" />
                <span>Phoenix y todo el condado de Maricopa</span>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-brand shrink-0 mt-0.5" />
                <span>
                  Inspecciones: 7 días a la semana, 7:00 AM – 7:00 PM
                  {inspection.daylightEs && (
                    <><br /><span className="text-white/60 text-xs">{inspection.daylightEs}</span></>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div id="estimado" className="section-dark text-white rounded-2xl p-8 md:p-10 shadow-2xl">
            <h2 className="font-heading text-3xl uppercase tracking-wide mb-2">Solicite Su Estimado Gratis</h2>
            <p className="text-white/60 text-sm mb-7">
              Llene el formulario y un miembro de nuestro equipo se comunicará con usted. También puede llamar o
              mandar un texto al {brand.phone}.
            </p>
            <LeadForm dark lang="es" />
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="kicker-dark">Nuestros Servicios</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              Todo Lo Que Su Casa Necesita
            </h2>
            <div className="brand-line mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicios.map((s) => (
              <div key={s.t} className="card-lift bg-white/5 border border-white/10 rounded-2xl p-7">
                <ShieldCheck size={26} className="text-brand mb-4" />
                <h3 className="font-heading text-lg uppercase tracking-wide mb-2">{s.t}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CIUDADES */}
      <section className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <span className="kicker">Áreas de Servicio</span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
            Trabajamos En Todo El Condado De Maricopa
          </h2>
          <div className="brand-line mx-auto mb-8" />
          <div className="flex flex-wrap justify-center gap-2.5">
            {ciudades.map((c) => (
              <span key={c.slug} className="px-4 py-2 rounded-full border border-ink/15 text-sm">{c.name}</span>
            ))}
          </div>
          <p className="text-muted-foreground mt-8">
            ¿No ve su ciudad? Llámenos o mándenos un texto al{" "}
            <a href={`tel:${brand.phoneRaw}`} className="text-brand-dark font-semibold hover:underline">{brand.phone}</a> —
            servimos todas las ciudades del condado.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 section-dark text-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-5">¿Listo Para Empezar?</h2>
          <p className="text-white/70 mb-8">
            Inspección y estimado gratis, sin compromiso. Atención en español los 7 días de la semana.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href={`tel:${brand.phoneRaw}`}
              className="inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-5 rounded-md transition-colors"
            >
              <Phone size={20} /> {brand.phone}
            </a>
            <a
              href="#estimado"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-brand hover:text-brand font-semibold uppercase tracking-wider text-sm px-8 py-5 rounded-md transition-colors"
            >
              Pedir Estimado <ArrowRight size={18} />
            </a>
          </div>
          <p className="text-white/60 text-xs mt-10">
            {brand.name} · Phoenix, AZ · {brand.phone}.{" "}
            <Link to="/" className="underline hover:text-brand">View this site in English</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Espanol;
